import { DATE_FORMAT } from 'config';
import { IconButton, InputAdornment, Popover, type SxProps, Tooltip, styled } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar, type DateCalendarProps } from '@mui/x-date-pickers/DateCalendar';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ComboBox } from 'components/inputs';
import { Calendar, X } from '@phosphor-icons/react';
import dayjs from 'dayjs';
import * as chrono from 'chrono-node';
import type { Dayjs } from 'dayjs';
import type { ComboBoxOption, CommonLabelSeparateProps } from 'components/inputs/types';
import { useTheme } from '@emotion/react';
import { t } from '@lingui/macro';

export type DatePickerProps = CommonLabelSeparateProps & {
  formatErrorHelperText?: string;
  id: string;
  placeholder?: string;
  label?: string;
  required?: boolean;
  onChange?: (
    event: React.SyntheticEvent,
    date: Dayjs | null,
    reason: string,
    stringDate: string,
    error: boolean
  ) => void;
  onBlur?: (date: Dayjs | null, stringDate: string, error: boolean) => void;
  inputVariant?: 'outlined' | 'standard' | 'filled';
  initialDate?: string;
  options?: ComboBoxOption[];
  onKeyDown?: (
    event: React.KeyboardEvent<HTMLDivElement> & { defaultMuiPrevented?: boolean }
  ) => void;
  sx?: SxProps;
  hideClear?: boolean;
  isStopPropagationOnKeyDown?: boolean;
  disabled?: boolean;
  shouldDisableDate?: DateCalendarProps<Dayjs>['shouldDisableDate'];
  /** When true, the calendar popover opens automatically when the component mounts */
  autoOpen?: boolean;
  /** Called when the calendar is closed by the user (backdrop click or Escape) */
  onCalendarClose?: () => void;
  /** When 'right', the calendar opens to the right of the input (e.g. for table cells). Default 'below'. */
  calendarPosition?: 'below' | 'right';
  /** Props passed to the underlying input (e.g. startAdornment for "Mixed values" chip) */
  InputProps?: {
    startAdornment?: React.ReactNode;
    endAdornment?: React.ReactNode;
    [key: string]: unknown;
  };
  /** Optional footer rendered below the calendar inside the popover (e.g. bulk-edit "Set empty") */
  calendarFooter?: React.ReactNode;
  /**
   * When true, updates the visible input when `initialDate` changes from the parent
   * (e.g. form `setValues`). Default false — preserves legacy DatePicker behaviour elsewhere.
   */
  syncInitialDateFromProps?: boolean;
};

const calendarColor = '#B3B3B3';

/**
 * Parses a string into a Dayjs date object using natural language processing.
 * @param str - the string to parse into a date
 * @returns a Dayjs date object if the string is valid, null otherwise
 */
export const getDateFromValue = (str: string | undefined) => {
  if (!str) return null;
  const naturalDate = chrono.parseDate(str);
  const dayDate = dayjs(naturalDate);
  return dayDate?.isValid() ? dayDate : null;
};

/**
 * DatePicker component, part of Delogue design system.
 * A date input with natural language parsing, calendar popup, and autocomplete suggestions.
 * @param id - unique identifier for the date picker input element
 * @param placeholder - placeholder text to display when the input is empty
 * @param label - optional label to display above the input
 * @param required - whether the input is required, will show a * after the label
 * @param onChange - callback function to be called when the date value changes
 * @param onBlur - callback function to be called when the input loses focus
 * @param options - array of autocomplete options to display in the dropdown
 * @param inputVariant - visual variant of the input, either 'outlined', 'standard', or 'filled'
 * @param initialDate - initial date value as a string
 * @param sx - MUI sx prop for custom styling
 * @param onKeyDown - callback function to be called when a key is pressed
 * @param hideClear - whether to hide the clear button
 * @param isStopPropagationOnKeyDown - whether to stop event propagation on key down
 * @param formatErrorHelperText - custom error message to return when date format is invalid
 * @param helperText - helper text to display below the input
 * @param error - whether the input is in an error state
 * @param disabled - whether the input is disabled
 * @param shouldDisableDate - callback function to determine if a date should be disabled in the calendar
 */
export const DatePicker = ({
  id,
  placeholder,
  onChange,
  onBlur,
  options = [],
  inputVariant,
  initialDate = '',
  sx,
  onKeyDown,
  hideClear,
  isStopPropagationOnKeyDown,
  formatErrorHelperText = t`Invalid date format`,
  helperText,
  error: propsError,
  shouldDisableDate,
  autoOpen = false,
  onCalendarClose,
  calendarPosition = 'below',
  InputProps: inputPropsProp,
  calendarFooter,
  syncInitialDateFromProps = false,
  ...rest
}: DatePickerProps) => {
  const [inputValue, setInputValue] = useState<string | null>(initialDate);
  const [date, setDate] = useState<dayjs.Dayjs | null>(getDateFromValue(initialDate));
  const [error, setError] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [dateDescription, setDateDescription] = useState<string>(initialDate);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const dateFormat = 'DD-MMM-YYYY';
  const theme = useTheme();
  const calendarButtonRef = useRef<HTMLButtonElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hasInvalidInput = useRef(false);
  const prevInitialDateRef = useRef(initialDate);

  const handleOnChange = (
    event: React.SyntheticEvent,
    value: ComboBoxOption | null | string | ComboBoxOption[],
    reason: string,
    stringDate?: string
  ) => {
    if (Array.isArray(value)) return;
    if (value === null) return;
    if (reason === 'reset' && hasInvalidInput.current) return;
    const dateString = typeof value === 'string' ? value : value?.label;
    const newDate = getDateFromValue(dateString);
    setDateDescription(dateString);
    if (reason === 'selectOption') {
      setDate(newDate);
      if (newDate) {
        setError(false);
        hasInvalidInput.current = false;
      } else {
        setError(true);
        hasInvalidInput.current = true;
      }
    }

    if (reason === 'clear') {
      setError(false);
      hasInvalidInput.current = false;
    }

    onChange && onChange(event, newDate, reason, dateString, error);
  };

  const handleBlur = () => {
    const newDate = getDateFromValue(dateDescription || '');
    setInputValue(dateDescription);
    setDate(newDate);

    const isValidDate = newDate ? newDate.isValid() : false;
    if (!isValidDate && dateDescription) {
      setError(true);
      hasInvalidInput.current = true;
    } else {
      setInputValue(newDate?.format(dateFormat) || '');
      setDateDescription(newDate?.format(dateFormat) || '');
      setError(false);
      hasInvalidInput.current = false;
    }
    const stringDateForCallback =
      isValidDate && newDate ? newDate.format(dateFormat) : dateDescription || '';
    onBlur?.(newDate, stringDateForCallback, isValidDate);
    if (anchorEl && !isCalendarOpen) {
      setAnchorEl(null);
    }
  };

  const handleCalendarChange = (newDate: Dayjs) => {
    const formattedDate = newDate.format(dateFormat);
    setDate(newDate);
    setInputValue(formattedDate);
    setDateDescription(formattedDate);
    setError(false);
    hasInvalidInput.current = false;
    setIsCalendarOpen(false);
    onChange?.(
      { target: { value: formattedDate } } as unknown as React.SyntheticEvent,
      newDate,
      'selectOption',
      formattedDate,
      false
    );
    onBlur?.(newDate, formattedDate, true);
  };

  const handleCalendarToggle = (
    event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
    setIsCalendarOpen(true);
  };

  const handleCloseCalendar = () => {
    setIsCalendarOpen(false);
    onCalendarClose?.();
  };

  const handleClearInput = () => {
    setInputValue('');
    setDateDescription('');
    setError(false);
    hasInvalidInput.current = false;
    setDate(null);
    onBlur?.(null, '', false);
    onChange?.(
      { target: { value: undefined } } as unknown as React.ChangeEvent<HTMLInputElement>,
      null,
      'onClear',
      '',
      false
    );
  };
  const onkeydown: DatePickerProps['onKeyDown'] = event => {
    if (isStopPropagationOnKeyDown) {
      event.stopPropagation();
    }
    if (event.key === 'Enter') {
      handleBlur();
    }
    if (onKeyDown) onKeyDown(event);
  };

  const dateValue = useMemo(() => {
    return inputValue ? getDateFromValue(inputValue)?.format(dateFormat) ?? '' : '';
  }, [inputValue, dateFormat]);

  useEffect(() => {
    if (!inputValue && initialDate && inputValue !== null) {
      setInputValue(initialDate);
    } else if (!initialDate && (inputValue || error)) {
      // Reset internal state when initialDate is cleared externally (e.g., form cancel/reset)
      setInputValue('');
      setDateDescription('');
      setDate(null);
      setError(false);
    } else if (
      syncInitialDateFromProps &&
      initialDate &&
      initialDate !== prevInitialDateRef.current
    ) {
      const parsed = getDateFromValue(initialDate);
      if (parsed?.isValid()) {
        const formatted = parsed.format(dateFormat);
        setInputValue(formatted);
        setDateDescription(formatted);
        setDate(parsed);
        setError(false);
        hasInvalidInput.current = false;
      }
    }
    if (syncInitialDateFromProps) {
      prevInitialDateRef.current = initialDate;
    }
  }, [initialDate, dateFormat, syncInitialDateFromProps]);

  useEffect(() => {
    if (autoOpen && calendarButtonRef.current && !hasAutoOpened) {
      setAnchorEl(calendarButtonRef.current);
      setIsCalendarOpen(true);
      setHasAutoOpened(true);
    }
  }, [autoOpen, hasAutoOpened]);

  const handleContainerClick = (e: React.MouseEvent) => {
    // Don't open if clicking the clear button
    if ((e.target as HTMLElement).closest('.clear-input')) {
      return;
    }
    // Don't open if clicking the text input (let user type)
    if ((e.target as HTMLElement).closest('input')) {
      return;
    }
    if (calendarButtonRef.current && !isCalendarOpen) {
      setAnchorEl(calendarButtonRef.current);
      setIsCalendarOpen(true);
    }
  };

  return (
    <Container ref={containerRef} onClick={handleContainerClick}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <ComboBox
          {...rest}
          sx={{ ...sx, paddingRight: '0' }}
          value={dateValue}
          inputValue={dateDescription}
          freeSolo
          error={error || propsError}
          helperText={error ? formatErrorHelperText : propsError ? helperText : ''}
          id="date-picker"
          placeholder={placeholder}
          options={options}
          onChange={(event, value, reason) => handleOnChange(event, value, reason)}
          onInputChange={(event, value, reason) => handleOnChange(event, value, reason)}
          onKeyDown={event => onkeydown(event)}
          inputVariant={inputVariant}
          clearOnBlur={false}
          onBlur={() => handleBlur()}
          InputProps={{
            ...inputPropsProp,
            endAdornment: (
              <InputAdornment position="end" sx={{ gap: '6px' }}>
                {inputValue && !hideClear && (
                  <IconButton
                    className="clear-input"
                    aria-label="clear input"
                    onClick={handleClearInput}
                    onKeyDown={handleClearInput}
                  >
                    <X size={20} />
                  </IconButton>
                )}
                <IconButton
                  ref={calendarButtonRef}
                  aria-label="open up calendar"
                  edge="end"
                  onClick={handleCalendarToggle}
                  onKeyDown={handleCalendarToggle}
                >
                  <Tooltip title={date?.isValid() && date?.format(DATE_FORMAT)}>
                    <Calendar
                      color={error ? theme.palette.border.error : calendarColor}
                      style={{ minWidth: 20 }}
                      size={20}
                    />
                  </Tooltip>
                </IconButton>
                <Popover
                  data-testid="calendar-popover"
                  open={isCalendarOpen}
                  anchorEl={anchorEl}
                  onClose={handleCloseCalendar}
                  disableScrollLock
                  disableAutoFocus={autoOpen}
                  slotProps={{
                    paper: { elevation: 8 },
                  }}
                  transformOrigin={
                    calendarPosition === 'right'
                      ? { vertical: 'top', horizontal: 'left' }
                      : { vertical: 'top', horizontal: 'center' }
                  }
                  anchorOrigin={
                    calendarPosition === 'right'
                      ? { vertical: 'top', horizontal: 'right' }
                      : { vertical: 'bottom', horizontal: 'center' }
                  }
                >
                  <DateCalendar
                    shouldDisableDate={shouldDisableDate}
                    value={date}
                    onChange={handleCalendarChange}
                  />
                  {calendarFooter}
                </Popover>
              </InputAdornment>
            ),
          }}
        />
      </LocalizationProvider>
    </Container>
  );
};

const Container = styled('div')`
  width: 100%;
  .clear-input {
    pointer-events: none;
    opacity: 0;
  }

  &:focus-within,
  &:hover {
    .clear-input {
      pointer-events: auto;
      opacity: 1;
    }
  }

  &&& .MuiInputBase-adornedEnd {
    padding-right: 20px;
  }
`;
